import {
    Grid, Typography, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField,
    FormControl,
    MenuItem,
    Select,
    InputLabel,
    Tooltip,
    Chip
} from "@mui/material";
import { useEffect, useState } from "react";
import { MAIN_URL } from "../../../Configurations/Urls";
import axios from "axios";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import { format } from "timeago.js";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";


const getStatusStyles = (status) => {
    switch (status) {
        case "Pending":
            return { backgroundColor: "#FFF4CC", color: "#B38700" }; // yellow
        case "In Progress":
            return { backgroundColor: "#D6E4FF", color: "#084298" }; // blue
        case "In Review":
            return { backgroundColor: "#EDE7F6", color: "#5E35B1" }; // purple
        case "Approved":
            return { backgroundColor: "#D1F2EB", color: "#0E6251" }; // green
        case "Rejected":
            return { backgroundColor: "#FADBD8", color: "#922B21" }; // red
        case "Completed":
            return { backgroundColor: "#D4EFDF", color: "#1D8348" }; // dark green
        case "Blocked":
            return { backgroundColor: "#FDEDEC", color: "#C0392B" }; // strong red
        default:
            return { backgroundColor: "#E0E0E0", color: "#424242" }; // grey
    }
};

const formatMinutes = (value) => {
    if (!value || value === 0) return "0m";

    value = Number(value);

    // Split minutes (before decimal) & seconds (after decimal)
    const minutes = Math.floor(value);
    const decimal = value - minutes;

    // Convert decimal fractional part into seconds (two digits max)
    const seconds = Math.round(decimal * 100);

    // Convert full minutes to hours/mins
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    let result = "";

    if (hours > 0) {
        result += `${hours}h `;
    }
    if (mins > 0) {
        result += `${mins}m `;
    }
    if (seconds > 0) {
        result += `${seconds}s`;
    }

    return result.trim() || "0m";
};

const formatTaskDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date)) return "-";
    return format(date);
};

const formatDate = (dt) => {
    if (!dt) return "-";
    let splitted = dt.split("-");
    return `${splitted[2]}/${splitted[1]}/${splitted[0]}`
}

function TasksMgmt() {

    let [status, setStatus] = useState("Completed")
    const { userData, reporting_manager_employees, reporting_manager_interns } = useAuthStore();
    const { Permission } = usePermissionDataStore();
    const org = userData?.organization;
    const loginUserOrgPersonId = userData?.loginuser?.[0]?.organization_person_id;
    
    // Check if user has VIEW_SELF_TASK permission - if yes, show only login user's tasks
    const hasViewSelfTaskPermission = Permission && Permission.includes("VIEW_SELF_TASK");

    const [tasks, setTasks] = useState([]);

    const [selectedDate, setSelectedDate] = useState(
        new Date().toISOString().slice(0, 10)
    );


    // popup
    const [openModal, setOpenModal] = useState(false);
    const [currentAction, setCurrentAction] = useState(""); // start / pause / stop
    const [currentTaskId, setCurrentTaskId] = useState(null);
    const [currentScTaskId, setCurrentScTaskId] = useState(null);
    const [remarks, setRemarks] = useState("");

    const [tasksFetchedAt, setTasksFetchedAt] = useState(null);
    const [, setTick] = useState(0);

    const fetchEmployeeTasks = async () => {
        try {


            // Convert selectedDate (YYYY-MM-DD) → Date object
            const selected = new Date(selectedDate);

            // Cutoff: 26 Dec 2025 (month is 0-based)
            const cutoffDate = new Date(2025, 11, 26);

            // Normalize time (important!)
            selected.setHours(0, 0, 0, 0);
            cutoffDate.setHours(0, 0, 0, 0);

            const endpoint =
                selected >= cutoffDate
                    ? "employee-tasks-latest-v1"
                    : "employee-tasks-latest";


            const params = {
                organization_id: org?.organization_id,
                organization_user_id: userData?.application_user_id,
                per_page: 100,
                date: selectedDate
            };
            
            // If VIEW_SELF_TASK permission exists, show only login user's tasks; otherwise show all org tasks
            if (hasViewSelfTaskPermission && loginUserOrgPersonId != null && loginUserOrgPersonId !== "") {
                params.organization_person_ids = [Number(loginUserOrgPersonId)];
            }
            
            const res = await axios.get(
                `${MAIN_URL}/api/project-management/project-tasks/${endpoint}`,
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                    params,
                }
            );

            const rows = res.data?.data || [];

            const datafinal = rows.map(item => ({
                task_id: item.organization_project_task_id || item.task_id, // Backend returns organization_project_task_id
                organization_project_task_schedule_id: item.organization_project_task_schedule_id,
                task_title: item.task_title,
                status: item.status,
                last_update: item.last_update, // Already formatted by backend
                total_time_spent: item.total_time_spent || 0,
                estimated_minutes: item.estimated_minutes || 0,
                total_estimated_minutes: item.total_estimated_minutes || 0,
                actual_minutes_taken: item.actual_minutes_taken || 0,
                assigned_organization_person: item.assigned_organization_person,
                scheduled_date: item.scheduled_date,
                parent_task: item.parent_task,
                scheduled_time: item.scheduled_time,
                started_date: item.started_date,
                started_time: item.started_time,
                quantity: item.quantity,
                ended_date: item.ended_date,
                ended_time: item.ended_time,
                time_logs: item.time_logs,
                project: item.project
            }));

            setTasks(datafinal);
            setTasksFetchedAt(Date.now());

        } catch (err) {
            console.error("Error loading tasks", err);
        }
    };

    useEffect(() => {
        if (org?.organization_id) {
            fetchEmployeeTasks();
        }
    }, [org?.organization_id, selectedDate, loginUserOrgPersonId, hasViewSelfTaskPermission]);

    // Live tick with setTimeout: re-render every 1s while any task is In Progress so Logged Time updates
    useEffect(() => {
        const hasInProgress = tasks.some((t) => t.status === "In Progress");
        if (!hasInProgress) return;

        let timeoutId;
        const scheduleTick = () => {
            timeoutId = setTimeout(() => {
                setTick((t) => t + 1);
                scheduleTick();
            }, 1000);
        };
        scheduleTick();

        return () => clearTimeout(timeoutId);
    }, [tasks]);

    // Periodic refetch every 60s so other viewers (e.g. when another person has page open) see status and time updates
    useEffect(() => {
        if (!org?.organization_id) return;

        const intervalId = setInterval(() => {
            fetchEmployeeTasks();
        }, 60000);

        return () => clearInterval(intervalId);
    }, [org?.organization_id, selectedDate, loginUserOrgPersonId, hasViewSelfTaskPermission]);

    const getDisplayMinutes = (row) => {
        if (row.status !== "In Progress" || tasksFetchedAt == null) {
            return row.actual_minutes_taken;
        }
        const elapsedSinceFetch = (Date.now() - tasksFetchedAt) / 60000;
        return (row.actual_minutes_taken || 0) + elapsedSinceFetch;
    };

    // open modal
    const handleActionClick = (organization_project_task_schedule_id, taskId, actionType) => {
        setCurrentTaskId(taskId);
        setCurrentScTaskId(organization_project_task_schedule_id);
        setCurrentAction(actionType);
        setRemarks("");
        setOpenModal(true);
    };

    // submit action
    const handleSubmitAction = async () => {

        console.log("currentTaskId is:", currentTaskId);

        try {
            // Build the request payload
            const payload = {
                task_id: parseInt(currentTaskId, 10), // Ensure it's an integer
                action: currentAction,
                remarks: remarks || "",
                application_user_id: parseInt(userData?.application_user_id, 10),
                organization_id: parseInt(org?.organization_id, 10),
                selectedDate: selectedDate
            };

            // Only add status when stopping
            if (currentAction === "stop") {
                payload.status = status;
            }


            // Convert selectedDate (YYYY-MM-DD) → Date object
            const selected = new Date(selectedDate);

            // Cutoff: 26 Dec 2025 (month is 0-based)
            const cutoffDate = new Date(2025, 11, 26);

            // Normalize time (important!)
            selected.setHours(0, 0, 0, 0);
            cutoffDate.setHours(0, 0, 0, 0);

            const endpoint =
                selected >= cutoffDate
                    ? "update-task-v1"
                    : "update-task";

            await axios.post(
                `${MAIN_URL}/api/project-management/project-tasks/${endpoint}`,
                payload,
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
                }
            );

            setOpenModal(false);
            setRemarks("");
            setStatus("Completed"); // Reset to default
            fetchEmployeeTasks();
        } catch (error) {
            console.error("Failed to update task:", error);
            const errorMsg = error.response?.data?.message || error.message;
            alert("Action failed: " + errorMsg);
        }
    };

    console.log('tasks is:', tasks);

    return (
        <Grid item xs={12} md={12}>
            <Card elevation={3}>
                <CardContent>
                    <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>

                        {/* Left side - Heading */}
                        <Grid item>
                            <Typography variant="h6" fontWeight="bold">
                                My Assigned Tasks
                            </Typography>
                        </Grid>

                        {/* Right side - Date controls */}
                        <Grid item>
                            <Grid container alignItems="center" spacing={1}>

                                {/* Back button */}
                                <Grid item>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            const prev = new Date(selectedDate);
                                            prev.setDate(prev.getDate() - 1);
                                            setSelectedDate(prev.toISOString().slice(0, 10));
                                        }}
                                    >
                                        <ArrowBackIosNew />
                                    </Button>
                                </Grid>

                                {/* Date Picker */}
                                <Grid item>
                                    <TextField
                                        type="date"
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ minWidth: 150 }}
                                    />
                                </Grid>

                                {/* Forward button */}
                                <Grid item>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            const next = new Date(selectedDate);
                                            next.setDate(next.getDate() + 1);
                                            setSelectedDate(next.toISOString().slice(0, 10));
                                        }}
                                    >
                                        <ArrowForwardIos />
                                    </Button>
                                </Grid>

                            </Grid>
                        </Grid>
                    </Grid>


                    <TableContainer>



                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><b>Project</b></TableCell>
                                    <TableCell><b>Task Title</b></TableCell>
                                    <TableCell><b>Qty.</b></TableCell>
                                    <TableCell><b>Schedule Date</b></TableCell>
                                    <TableCell><b>Allocated Time</b></TableCell>
                                    <TableCell><b>Status</b></TableCell>
                                    <TableCell><b>Logged Time</b></TableCell>
                                    <TableCell><b>Actions</b></TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {tasks?.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            No tasks assigned
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    tasks?.map((row) => {

                                        const showStart = row.status === "Paused" || row.status === "Pending";
                                        const showPause = row.status === "In Progress";
                                        const showStop = row.status === "In Progress" || row.status === "Paused";

                                        return (
                                            <TableRow key={row.task_id}>
                                                <TableCell>{row?.project?.project_name}</TableCell>
                                                <TableCell>{row.task_title}</TableCell>
                                                <TableCell>{row.quantity}</TableCell>


                                                <TableCell>{formatDate(row.scheduled_date)}</TableCell>

                                                <TableCell>
                                                    {row.total_estimated_minutes
                                                        ? `${formatMinutes(row.total_estimated_minutes)}`
                                                        : "—"}
                                                </TableCell>

                                                <TableCell>
                                                    <Chip
                                                        label={row.status}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 600,
                                                            textTransform: "capitalize",
                                                            ...getStatusStyles(row.status),
                                                        }}
                                                    />
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        color:
                                                            getDisplayMinutes(row) > row.total_estimated_minutes
                                                                ? "red"
                                                                : "green",
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {getDisplayMinutes(row)
                                                        ? `${formatMinutes(getDisplayMinutes(row))}`
                                                        : "—"}
                                                </TableCell>




                                                <TableCell>

                                                    {/* START BUTTON */}
                                                    {showStart && (
                                                        <Tooltip
                                                            title={
                                                                row.parent_task != null && row.parent_task.status !== "Completed"
                                                                    ? `Parent task (${row.parent_task.task_title}) must be completed first`
                                                                    : ""
                                                            }
                                                            disableHoverListener={
                                                                !(row.parent_task != null && row.parent_task.status !== "Completed")
                                                            }
                                                        >
                                                            <span>
                                                                <Button
                                                                    disabled={row.parent_task != null && row.parent_task.status !== "Completed"}
                                                                    sx={{
                                                                        mr: 1,
                                                                        cursor: (row.parent_task != null && row.parent_task.status !== "Completed")
                                                                            ? "not-allowed"
                                                                            : "pointer"
                                                                    }}
                                                                    variant="contained"
                                                                    color="success"
                                                                    size="small"
                                                                    onClick={() => handleActionClick(row.organization_project_task_schedule_id, row.task_id, "start")}
                                                                >
                                                                    Start
                                                                </Button>
                                                            </span>
                                                        </Tooltip>
                                                    )}

                                                    {/* PAUSE BUTTON */}
                                                    {showPause && (
                                                        <Tooltip
                                                            title={
                                                                row.parent_task != null && row.parent_task.status !== "Completed"
                                                                    ? `Parent task (${row.parent_task.parent_title}) must be completed first`
                                                                    : ""
                                                            }
                                                            disableHoverListener={
                                                                !(row.parent_task != null && row.parent_task.status !== "Completed")
                                                            }
                                                        >
                                                            <span>
                                                                <Button
                                                                    disabled={row.parent_task != null && row.parent_task.status !== "Completed"}
                                                                    sx={{
                                                                        mr: 1,
                                                                        cursor: (row.parent_task != null && row.parent_task.status !== "Completed")
                                                                            ? "not-allowed"
                                                                            : "pointer"
                                                                    }}
                                                                    variant="contained"
                                                                    color="warning"
                                                                    size="small"
                                                                    onClick={() => handleActionClick(row.organization_project_task_schedule_id, row.task_id, "pause")}
                                                                >
                                                                    Pause
                                                                </Button>
                                                            </span>
                                                        </Tooltip>
                                                    )}

                                                    {/* STOP BUTTON */}
                                                    {showStop && (
                                                        <Tooltip
                                                            title={
                                                                row.parent_task != null && row.parent_task.status !== "Completed"
                                                                    ? `Parent task (${row.parent_task.parent_title}) must be completed first`
                                                                    : ""
                                                            }
                                                            disableHoverListener={
                                                                !(row.parent_task != null && row.parent_task.status !== "Completed")
                                                            }
                                                        >
                                                            <span>
                                                                <Button
                                                                    disabled={row.parent_task != null && row.parent_task.status !== "Completed"}
                                                                    sx={{
                                                                        cursor: (row.parent_task != null && row.parent_task.status !== "Completed")
                                                                            ? "not-allowed"
                                                                            : "pointer"
                                                                    }}
                                                                    variant="contained"
                                                                    color="error"
                                                                    size="small"
                                                                    onClick={() => handleActionClick(row.organization_project_task_schedule_id, row.task_id, "stop")}
                                                                >
                                                                    Stop
                                                                </Button>
                                                            </span>
                                                        </Tooltip>
                                                    )}

                                                </TableCell>

                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                </CardContent>
            </Card>


            {/* ===== POPUP ===== */}
            <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth>
                <DialogTitle>
                    {currentAction === "start" && "Start Task"}
                    {currentAction === "pause" && "Pause Task"}
                    {currentAction === "stop" && "Stop Task"}
                </DialogTitle>

                <DialogContent>
                    {currentAction === "stop" && (
                        <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                            <InputLabel>Status After Stop</InputLabel>
                            <Select
                                label="Status After Stop"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <MenuItem value="In Review">In Review</MenuItem>
                                <MenuItem value="Approved">Approved</MenuItem>
                                <MenuItem value="Rejected">Rejected</MenuItem>
                                <MenuItem value="Completed">Completed</MenuItem>
                                <MenuItem value="Blocked">Blocked</MenuItem>
                            </Select>
                        </FormControl>
                    )}

                    <TextField
                        fullWidth
                        label="Remarks"
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        multiline
                        rows={3}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>


                <DialogActions>
                    <Button onClick={() => setOpenModal(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmitAction}>
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
}

export default TasksMgmt;